/* t7-2b Bloque B (#518): EL CHAT "PREGUNTALE AL PANEL", LO QUE NO ES PANTALLA.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   La unica puerta del frontend a las rutas del chat (el servidor vive en
   `backend/routes/grupo_chat.py`, Bloque A) y las funciones puras que usa el cajon
   (`components/PanelChatCajon.js`):
     - las cinco llamadas del contrato (listar, crear, leer, mandar la pregunta, borrar);
     - partir el texto del panel en bloques por las marcas de sugerencia: el servidor ya lo
       manda partido (`bloques`), y esto es la GUARDA DE CLIENTE para que una marca jamas se vea
       cruda (un mensaje sin bloques, o un bloque con una marca colada);
     - validar los adjuntos ANTES de mandar (hasta 3, hasta 5 MB, y los tipos que el servidor
       lee; HEIC no, porque el camino de imagenes del profe no lo lee);
     - la llave del texto de cada error, por el CODIGO que manda el servidor;
     - la etiqueta de un chat en el selector ("Hoy: titulo" o "Mie 23 sep: titulo").

   LA PREGUNTA VIAJA SIEMPRE EN UN FormData, con o sin archivos: el contrato es multipart, y un
   FormData le da a la peticion el techo de las subidas de `lib/api.js` (3 minutos) en vez de
   los 10 s de una lectura; los dos pasos del modelo pueden tardar mas que eso. */

import { diasDesde } from './avanceDelGrupo';
import { localeDeFecha } from './fechaDeSesion';

// El cliente HTTP se pide al LLAMAR, no al importar (el mismo motivo, medido, que en
// `lib/materialDelGrupo.js`): `lib/api.js` arrastra `src/i18n.js`.
function cliente() {
  return require('./api').default;
}

/* -- los topes del contrato --------------------------------------------------- */

export const MAX_ARCHIVOS = 3;
export const MAX_BYTES = 5 * 1024 * 1024;
export const EXTENSIONES = ['.pdf', '.docx', '.pptx', '.jpg', '.jpeg', '.png'];
export const ACEPTA = EXTENSIONES.join(',');
export const TOPE_PREGUNTA = 4000;

/* -- las llamadas ------------------------------------------------------------- */

const rutaChats = (grupo) => `/grupos/${grupo}/chats`;

export async function listarChats(grupo) {
  const res = await cliente().get(rutaChats(grupo));
  const chats = res && res.data && res.data.chats;
  return Array.isArray(chats) ? chats : [];
}

export async function crearChat(grupo) {
  const res = await cliente().post(rutaChats(grupo));
  return res && res.data ? res.data.chat : null;
}

export async function leerChat(grupo, chatId) {
  const res = await cliente().get(`${rutaChats(grupo)}/${chatId}`);
  return res && res.data ? res.data.chat : null;
}

/** `idioma` (opcional) viaja como `?lang=`: la ruta contesta en el idioma de la PANTALLA (como
    el avance, que tambien lo manda); sin el, la ruta usa el idioma del perfil del profe. */
export async function mandarPregunta(grupo, chatId, pregunta, archivos, idioma) {
  const cuerpo = new FormData();
  cuerpo.append('pregunta', pregunta);
  for (const archivo of archivos || []) cuerpo.append('archivos', archivo, archivo.name);
  const res = await cliente().post(`${rutaChats(grupo)}/${chatId}/mensajes`, cuerpo, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...(idioma ? { params: { lang: idioma } } : {}),
  });
  return res ? res.data : null;
}

export async function borrarChat(grupo, chatId) {
  await cliente().delete(`${rutaChats(grupo)}/${chatId}`);
}

/* -- los bloques (las marcas de sugerencia nunca crudas) ---------------------- */

// [SUGERENCIA] abre y [/SUGERENCIA] cierra; se toleran minusculas y espacios dentro del
// corchete, por si el modelo se sale de la letra.
const MARCA = /\[\s*(\/?)\s*sugerencia\s*\]/gi;

/** Parte un texto en bloques `{tipo: 'dato'|'sugerencia', texto}`. Una marca que no cambia de
    tipo (un cierre suelto, una apertura repetida) se quita sin partir el renglon; una
    sugerencia que abre y no cierra llega hasta el final; los bloques vacios no salen. */
export function partirEnBloques(texto) {
  const s = texto == null ? '' : String(texto);
  const bloques = [];
  let tipo = 'dato';
  let acumulado = '';
  let desde = 0;
  const cerrar = () => {
    const limpio = acumulado.trim();
    if (limpio) bloques.push({ tipo, texto: limpio });
    acumulado = '';
  };
  const juntar = (trozo) => {
    // Donde se quito una marca suelta quedan dos blancos: se deja uno.
    if (acumulado && /[ \t]$/.test(acumulado) && /^[ \t]/.test(trozo)) {
      acumulado = acumulado.replace(/[ \t]+$/, ' ') + trozo.replace(/^[ \t]+/, '');
    } else {
      acumulado += trozo;
    }
  };
  MARCA.lastIndex = 0;
  let m = MARCA.exec(s);
  while (m !== null) {
    juntar(s.slice(desde, m.index));
    const siguiente = m[1] ? 'dato' : 'sugerencia';
    if (siguiente !== tipo) {
      cerrar();
      tipo = siguiente;
    }
    desde = m.index + m[0].length;
    m = MARCA.exec(s);
  }
  juntar(s.slice(desde));
  cerrar();
  return bloques;
}

/** Los bloques con que se pinta un mensaje del panel: los del servidor si llegan (un bloque de
    dato con marcas dentro se parte; a uno de sugerencia se le quitan), y si no, el texto
    partido con la misma regla. */
export function bloquesDelMensaje(mensaje) {
  if (!mensaje) return [];
  const delServidor = Array.isArray(mensaje.bloques) ? mensaje.bloques : [];
  if (delServidor.length === 0) return partirEnBloques(mensaje.texto);
  const bloques = [];
  for (const b of delServidor) {
    if (!b) continue;
    if (b.tipo === 'sugerencia') {
      const limpio = String(b.texto == null ? '' : b.texto).replace(MARCA, ' ').replace(/[ \t]{2,}/g, ' ').trim();
      if (limpio) bloques.push({ tipo: 'sugerencia', texto: limpio });
    } else {
      bloques.push(...partirEnBloques(b.texto));
    }
  }
  return bloques;
}

/* -- los adjuntos -------------------------------------------------------------- */

function extension(nombre) {
  const s = String(nombre || '').toLowerCase();
  const i = s.lastIndexOf('.');
  return i >= 0 ? s.slice(i) : '';
}

function problemaDe(archivo) {
  if (!EXTENSIONES.includes(extension(archivo && archivo.name))) return 'ilegible';
  if ((archivo.size || 0) > MAX_BYTES) return 'archivo_grande';
  return null;
}

/** Suma los `nuevos` a los `actuales` que ya estaban elegidos: entra lo que se puede leer, lo
    que pesa hasta 5 MB y hasta llegar a 3. Devuelve la lista y el PRIMER problema (o null). */
export function agregarAdjuntos(actuales, nuevos) {
  const lista = [...(actuales || [])];
  let error = null;
  for (const archivo of nuevos || []) {
    const problema = problemaDe(archivo);
    if (problema) {
      if (!error) error = { codigo: problema, nombre: archivo.name };
      continue;
    }
    if (lista.length >= MAX_ARCHIVOS) {
      if (!error) error = { codigo: 'demasiados', nombre: archivo.name };
      continue;
    }
    lista.push(archivo);
  }
  return { lista, error };
}

/** La ultima mirada antes de mandar (la lista pudo armarse por otro camino). */
export function revisarAdjuntos(archivos) {
  const lista = archivos || [];
  if (lista.length > MAX_ARCHIVOS) return { codigo: 'demasiados' };
  for (const archivo of lista) {
    const problema = problemaDe(archivo);
    if (problema) return { codigo: problema, nombre: archivo.name };
  }
  return null;
}

/* -- los errores ---------------------------------------------------------------- */

const LLAVE = 'groups.avance.chat';
const POR_CODIGO = {
  chat_no_disponible: 'errores.no_disponible',
  chat_archivo_grande: 'errores.archivo_grande',
  chat_demasiados_archivos: 'errores.demasiados',
  chat_archivo_ilegible: 'errores.ilegible',
  chat_lleno: 'errores.nuevo_chat',
  chat_tope_adjuntos: 'errores.nuevo_chat',
  // t7-3 remate 7 (pasada 2, regla 7): el chat se borro en otra pestana; "No pude contestar" dejaba al profe en un ciclo
  chat_no_encontrado: 'errores.nuevo_chat',
};
const DEL_CLIENTE = {
  demasiados: 'errores.demasiados',
  archivo_grande: 'errores.archivo_grande',
  ilegible: 'errores.ilegible',
};
const FALLO = { llave: `${LLAVE}.fallo` };

/** `{llave, params?}` del texto que toca a un error del servidor. Manda el CODIGO; sin codigo,
    solo un 413 (el borde corto un archivo de mas) tiene texto propio; todo lo demas (el 502,
    el freno por minuto, la red, un codigo sin texto ratificado) es "No pude contestar". */
export function errorDelServidor(err) {
  const respuesta = err && err.response;
  const detalle = respuesta && respuesta.data ? respuesta.data.detail : null;
  const codigo = detalle && typeof detalle === 'object' ? detalle.error : null;
  if (codigo === 'chat_tope_diario') {
    const n = Number(detalle.n);
    if (!Number.isInteger(n) || n < 0) return FALLO;
    return { llave: `${LLAVE}.errores.tope_diario`, params: { count: n, n } };
  }
  if (codigo && POR_CODIGO[codigo]) return { llave: `${LLAVE}.${POR_CODIGO[codigo]}` };
  if (!codigo && respuesta && respuesta.status === 413) return { llave: `${LLAVE}.errores.archivo_grande` };
  return FALLO;
}

/** t7-3 remate 7 (pasada 4, regla 7): el error de una ACCION que no es contestar (abrir la lista, leer un chat, "Nuevo
    chat", "Borrar"): el texto propio de su codigo si lo hay; si no, los avisos de la casa (sin respuesta, "Error de
    red"; el freno, "Demasiadas solicitudes..."; lo demas, "Algo salio mal..."). "No pude contestar" queda para mandar. */
export function errorDeAccion(err) {
  const propio = errorDelServidor(err);
  if (propio !== FALLO) return propio;
  const respuesta = err && err.response;
  if (!respuesta) return { llave: 'common:errors.network' };
  if (respuesta.status === 429) return { llave: 'common:errors.rate_limit_generic' };
  return { llave: 'common:toasts.generic_error' };
}

/** `{llave}` del texto de un problema que el cliente vio antes de mandar. */
export function llaveDelCliente(problema) {
  const sub = problema && DEL_CLIENTE[problema.codigo];
  return sub ? { llave: `${LLAVE}.${sub}` } : FALLO;
}

/* -- la etiqueta del chat en el selector ------------------------------------------ */

/** "Mie 23 sep" / "Wed Sep 23": dia de la semana, dia y mes cortos, en el orden del idioma y
    sin la puntuacion de `Intl` ("mie 23 de sep" -> "Mie 23 sep"). */
export function fechaCorta(iso, idioma, opciones = {}) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  const partes = new Intl.DateTimeFormat(localeDeFecha(idioma), {
    weekday: 'short', day: 'numeric', month: 'short',
    ...(opciones.timeZone ? { timeZone: opciones.timeZone } : {}),
  }).formatToParts(d);
  const piezas = partes
    .filter((p) => p.type === 'weekday' || p.type === 'day' || p.type === 'month')
    .map((p) => p.value.replace(/\.$/, ''));
  if (piezas.length === 0) return '';
  piezas[0] = piezas[0].charAt(0).toUpperCase() + piezas[0].slice(1);
  return piezas.join(' ');
}

/** "Hoy: titulo" o "Mie 23 sep: titulo". Un chat sin titulo todavia (recien creado) se llama
    "Nuevo chat"; sin fecha legible, solo el titulo. */
export function etiquetaDelChat(t, chat, idioma, ahora = new Date()) {
  const titulo = String((chat && chat.titulo) || '').trim() || t(`${LLAVE}.nuevo`);
  const dias = diasDesde(chat && chat.actualizado, ahora);
  if (dias === null) return titulo;
  const cuando = dias <= 0 ? t(`${LLAVE}.hoy`) : fechaCorta(chat.actualizado, idioma);
  return cuando ? `${cuando}: ${titulo}` : titulo;
}

/** Ordena los chats como los manda el servidor: el actualizado mas reciente primero. */
export function ordenarChats(chats) {
  const ms = (c) => {
    const x = new Date(c && c.actualizado).getTime();
    return Number.isNaN(x) ? 0 : x;
  };
  return [...(chats || [])].sort((a, b) => ms(b) - ms(a));
}

/* -- la nota de paginas leidas ------------------------------------------------------ */

/* post-t7-1 M2: `paginasLeidas` se fue (solo la usaba su prueba; lo leido lo dice `loLeido`). */

/** t7-3 remate 8 (#591, C12): lo leido de cada adjunto RECORTADO, segun su archivo (por su extension): un PDF dice
    sus paginas ("Lei las primeras n paginas."), un PowerPoint sus diapositivas, y un Word o una foto "Lei la primera
    parte del documento." (no tienen paginas que contar). `[{llave, n}]`, la llave dentro de `groups.avance.chat`. */
export function loLeido(mensajeDelProfe) {
  const adjuntos = mensajeDelProfe && Array.isArray(mensajeDelProfe.adjuntos) ? mensajeDelProfe.adjuntos : [];
  return adjuntos
    .filter((a) => a && a.recortado)
    .map((a) => {
      const nombre = String(a.nombre || '').toLowerCase();
      const n = Number.isInteger(a.leidas) && a.leidas > 0
        ? a.leidas : Math.max(1, Math.floor((Number(a.caracteres) || 0) / 3000));
      if (nombre.endsWith('.pdf')) return { llave: 'leidas', n };
      if (nombre.endsWith('.pptx') || nombre.endsWith('.ppt')) return { llave: 'leidas_diapositivas', n };
      return { llave: 'leidas_parte', n: null };
    });
}

/* -- las sugerencias del cajon (t7-3 remate 8, #590) ---------------------------------- */

/** El tope de sugerencias a la vista. */
export const MAXIMO_DE_SUGERENCIAS = 4;
/** Un tema principal con un nombre de mas de esto no cabe en "Como explico ...?": sale "Que explico primero?". */
export const LARGO_MAXIMO_DEL_TEMA_EN_LA_SUGERENCIA = 40;
/** Los motivos que piden un recordatorio para practicar. */
const MOTIVOS_DE_RECORDAR = ['nunca', 'dejo', 'poco'];

/** Las sugerencias del cajon, con lo que el panel ya tiene cargado (el JSON del avance): `[{id, llave, params}]`, la
    llave dentro de `groups.avance.chat`. Las candidatas, en este orden, y salen las primeras CUATRO que apliquen:
      1. tema principal en rojo con nombre de 40 o menos: "Como explico (tema)?"; con nombre mas largo, o sin tema en
         rojo pero con alguien que ya practico: "Que explico primero?";
      2. alguien en riesgo: "Como va (el mas urgente)?";
      3. alguien en "nunca", "dejo" o "poco": "A quien le recuerdo practicar?";
      4. alguien mejoro: "Quien mejoro?";
      5. una publicacion activa y alguien que ya practico: "Estan listos para el examen?".
    Sin alumnos o sin publicaciones, ninguna. */
export function sugerenciasDelCajon(datos) {
  const g = (datos && datos.grupo) || {};
  if (!(Number(g.alumnos) > 0) || !(Number(g.cards_publicadas) > 0)) return [];
  const alumnos = Array.isArray(datos.alumnos) ? datos.alumnos : [];
  const alguienPractico = !!g.ultima_practica || alumnos.some((a) => a && a.nunca === false);
  const salida = [];
  const tema = datos.tema_principal && datos.tema_principal.tema;
  if (tema && tema.length <= LARGO_MAXIMO_DEL_TEMA_EN_LA_SUGERENCIA) {
    salida.push({ id: 'explico', llave: 'chip_explico', params: { tema, topic: tema } });
  } else if (tema || alguienPractico) {
    salida.push({ id: 'primero', llave: 'chip_primero', params: {} });
  }
  const motivos = Array.isArray(datos.riesgo_por_motivo) ? datos.riesgo_por_motivo : [];
  const urgente = motivos[0] && motivos[0].urgente && motivos[0].urgente.nombre;
  if (urgente) salida.push({ id: 'como-va', llave: 'chip_como_va', params: { nombre: urgente, name: urgente } });
  if (motivos.some((m) => m && MOTIVOS_DE_RECORDAR.includes(m.motivo) && Number(m.x) > 0)) {
    salida.push({ id: 'recuerdo', llave: 'chip_recuerdo', params: {} });
  }
  if (datos.mejora && Number(datos.mejora.mejoraron_n) > 0) salida.push({ id: 'mejoro', llave: 'chip_mejoro', params: {} });
  if (Number(g.cards_activas) > 0 && alguienPractico) salida.push({ id: 'listos', llave: 'chip_listos', params: {} });
  return salida.slice(0, MAXIMO_DE_SUGERENCIAS);
}

/* -- la caja de la pregunta (t7-3 remate 8, #591, C98) ------------------------------- */

/** La caja crece al escribir hasta 4 renglones (20 px de interlineado, 22 de relleno y 2 de borde) y despues se
    desliza por dentro. */
export const ALTO_DE_LA_CAJA = 44;
export const ALTO_MAXIMO_DE_LA_CAJA = 4 * 20 + 22 + 2;

/** El alto de la caja para un `scrollHeight` (que no cuenta el borde): `{alto, desliza}`. */
export function altoDeLaCaja(scrollHeight) {
  const pedido = (Number(scrollHeight) || 0) + 2;
  return { alto: Math.min(Math.max(ALTO_DE_LA_CAJA, pedido), ALTO_MAXIMO_DE_LA_CAJA), desliza: pedido > ALTO_MAXIMO_DE_LA_CAJA };
}
